import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import {
  APP_REGISTRY_API_PATHS,
  APP_REGISTRY_QUERY_KEY,
  getAppRegistryBaseUrl,
} from '@/constants/appRegistry';
import { useApiMutation } from './useApiMutation';

// PUT bulk enable/disable overrides for a user in one call. Body is a JSON array of
// { appuuid, enabled } items; only `enabled` is sent per item (partial update).
const useBulkUpdateAppRegistryOverrides = ({ useruuid }) => {
  const trans = useSelector(translator);
  const userApi = useSelector((state) => state.apis.userApi);
  const queryClient = useQueryClient();

  return useApiMutation(
    ({ items, successCallback = () => {}, errorCallback = () => {} }) =>
      new Promise((resolve, reject) => {
        userApi.apiClient.callApi(
          APP_REGISTRY_API_PATHS.USER_OVERRIDE_BULK,
          'PUT',
          { useruuid },
          {},
          {},
          {},
          items.map(({ appuuid, enabled }) => ({ appuuid, enabled })),
          [],
          ['application/json'],
          ['application/json'],
          Object,
          getAppRegistryBaseUrl(),
          apiCallback({
            translator: trans,
            successCallback: (data) => {
              successCallback(data);
              resolve(data);
            },
            errorCallback: (err) => {
              errorCallback(err);
              reject(err);
            },
          })
        );
      }),
    {
      // Every changed app's `enabled` state is already known locally (that's what we sent),
      // so patch the cache directly instead of invalidating and re-fetching the whole list.
      onSuccess: (_data, { items }) => {
        const enabledByAppuuid = new Map(items.map(({ appuuid, enabled }) => [appuuid, enabled]));
        queryClient.setQueryData([APP_REGISTRY_QUERY_KEY, useruuid], (apps) =>
          apps?.map((app) =>
            enabledByAppuuid.has(app.appuuid)
              ? { ...app, enabled: enabledByAppuuid.get(app.appuuid) }
              : app
          )
        );
      },
    }
  );
};

export default useBulkUpdateAppRegistryOverrides;
